import React, { useEffect, useState } from 'react';
import { X, Loader2, FileText, Image as ImageIcon, Paperclip, AlertCircle, Check } from 'lucide-react';
import { agentApi } from '../../api';
import type { AgentDocument } from '../../api/agent';
import { isSupportedMediaType } from '../../hooks/useFileAttachment';

/** Visual icon per document family. PDF/text → FileText; image → ImageIcon. */
function iconForType(type: string): React.ReactNode {
  if (type.startsWith('image/')) return <ImageIcon size={18} className="text-blue-500" />;
  return <FileText size={18} className="text-red-500" />;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Modal that lists the user's previously uploaded documents so they can be
 * referenced by ID in the next chat message. Checkbox-based multi-select —
 * confirming the selection calls `onSelect` once per picked document, which
 * the caller wires to `useFileAttachment.attachDocumentId()`.
 *
 * Only `status === 'ready'` (or legacy 'completed') rows are selectable —
 * documents still processing can't be referenced yet. Inactive (`is_active=false`)
 * docs are filtered server-side, but we also dim any that slipped through.
 *
 * Spec: docs/design/01-api-reference.md §5 Chat API — FileAttachment (document_id 모드).
 */
const DocumentPickerModal: React.FC<{
  userId: string;
  onClose: () => void;
  onSelect: (doc: { documentId: string; name: string; media_type: string }) => void;
  /** Max docs the user can still attach — used to cap the multi-select. */
  remainingSlots: number;
}> = ({ userId, onClose, onSelect, remainingSlots }) => {
  const [docs, setDocs] = useState<AgentDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    agentApi
      .listDocuments(userId, { limit: 100 })
      .then((res) => {
        if (cancelled) return;
        // Only show active, ready-to-reference docs. 'ready' / 'completed' both
        // observed in the wild; match either.
        const usable = res.documents.filter(
          (d) => d.is_active && (d.status === 'ready' || d.status === 'completed'),
        );
        setDocs(usable);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : '문서 목록을 불러오지 못했습니다');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ESC → close. Standard modal a11y affordance — without this, keyboard-only
  // users have no way to dismiss the overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size >= remainingSlots) {
        // Respect the attachment cap — don't silently drop, just refuse the click.
        return prev;
      } else {
        next.add(id);
      }
      return next;
    });
  };

  /** Whether this doc's MIME is accepted by the chat endpoint. Rows whose type
   * isn't supported are dimmed + disabled in the list — avoids a network round-trip
   * followed by a validation error the user has to recover from. */
  const isUsable = (doc: AgentDocument): boolean => isSupportedMediaType(doc.file.type);

  const confirm = () => {
    for (const id of selected) {
      const doc = docs.find((d) => d.id === id);
      if (!doc) continue;
      onSelect({
        documentId: doc.id,
        name: doc.file.name,
        media_type: doc.file.type,
      });
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      data-testid="doc-picker-modal"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Paperclip size={20} />
            업로드된 문서 참조
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600" data-testid="doc-picker-close">
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && (
            <div className="flex items-center justify-center py-12 text-slate-400 gap-2">
              <Loader2 size={18} className="animate-spin" />
              <span className="text-sm">문서 목록 로딩 중...</span>
            </div>
          )}

          {!loading && error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              {error}
            </div>
          )}

          {!loading && !error && docs.length === 0 && (
            <div className="text-center text-sm text-slate-400 py-12">
              처리 완료된 업로드 문서가 없습니다
              <br />
              <span className="text-xs">왼쪽 [문서] 탭에서 먼저 업로드·처리하세요</span>
            </div>
          )}

          {!loading && !error && docs.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {docs.map((doc) => {
                const isSelected = selected.has(doc.id);
                const usable = isUsable(doc);
                const atCap = !isSelected && selected.size >= remainingSlots;
                const disabled = atCap || !usable;
                return (
                  <li key={doc.id}>
                    <button
                      onClick={() => toggle(doc.id)}
                      disabled={disabled}
                      data-testid="doc-picker-item"
                      data-doc-id={doc.id}
                      data-selected={isSelected ? 'true' : 'false'}
                      data-unsupported={!usable ? 'true' : 'false'}
                      title={!usable ? '지원하지 않는 파일 형식 (JPEG/PNG/WebP/PDF만 가능)' : undefined}
                      className={`w-full flex items-center gap-3 py-2.5 px-2 rounded text-left transition-colors ${
                        isSelected
                          ? 'bg-blue-50'
                          : disabled
                          ? 'opacity-40 cursor-not-allowed'
                          : 'hover:bg-slate-50'
                      }`}
                    >
                      <span
                        className={`w-5 h-5 flex items-center justify-center rounded border ${
                          isSelected ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'
                        }`}
                      >
                        {isSelected && <Check size={14} />}
                      </span>
                      {iconForType(doc.file.type)}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate">{doc.file.name}</div>
                        <div className="text-xs text-slate-400">
                          {humanSize(doc.file.size)} · {doc.file.type}
                          {!usable && <span className="ml-1 text-amber-600">· 미지원</span>}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-200">
          <span className="text-xs text-slate-500">
            선택 {selected.size} / 최대 {remainingSlots}개
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900"
            >
              취소
            </button>
            <button
              onClick={confirm}
              disabled={selected.size === 0}
              data-testid="doc-picker-confirm"
              className="px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              추가
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPickerModal;
